import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Container } from '../../styles/components.js';
import { FaMobileAlt, FaBolt, FaRocket, FaPlug, FaCoins, FaPalette } from 'react-icons/fa';

const ServicesSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  background-color: ${({ theme }) => theme.colors.surface};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 650px;
  margin: 0 auto ${({ theme }) => theme.spacing.xl};
  position: relative;
  z-index: 1;
`;

const Label = styled.div`
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary}0a;
  padding: 5px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid ${({ theme }) => theme.colors.primary}18;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-size: clamp(1.8rem, 3.5vw, 2.6rem);
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textDark};
  margin-bottom: 1rem;
  letter-spacing: -0.03em;

  span {
    background: ${({ theme }) => theme.colors.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Desc = styled.p`
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.textGray};
  line-height: 1.7;
  margin-bottom: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  position: relative;
  z-index: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const SpotlightCardWrapper = styled(motion.div)`
  position: relative;
  padding: 2.25rem 2rem;
  background-color: ${({ theme }) => theme.colors.surfaceAlt};
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.3s ease, transform 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: ${({ theme }) => theme.colors.primary}25;
  }
`;

const SpotlightEffect = styled.div.attrs(props => ({
  style: {
    background: `radial-gradient(350px circle at ${props.x}px ${props.y}px, ${props.glowColor}, transparent 80%)`,
  },
}))`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.4s ease;

  ${SpotlightCardWrapper}:hover & {
    opacity: 1;
  }
`;

const IconContainer = styled.div`
  width: 56px;
  height: 56px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 1.75rem;
  position: relative;
  z-index: 1;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  svg {
    width: 24px;
    height: 24px;
    color: ${({ theme }) => theme.colors.primary};
    transition: color 0.3s ease;
  }

  ${SpotlightCardWrapper}:hover & {
    border-color: ${({ theme }) => theme.colors.primary}30;
    background: ${({ theme }) => theme.colors.gradient};
    transform: scale(1.05) rotate(-3deg);
    box-shadow: ${({ theme }) => theme.shadows.glow};
    
    svg {
      color: #fff;
    }
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.15rem;
  margin-bottom: 0.75rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textDark};
  position: relative;
  z-index: 1;
  letter-spacing: -0.02em;
`;

const ServiceDescription = styled.p`
  color: ${({ theme }) => theme.colors.textGray};
  margin-bottom: 0;
  line-height: 1.7;
  font-size: 0.9rem;
  position: relative;
  z-index: 1;
`;

const services = [
  {
    icon: <FaMobileAlt />,
    title: 'React Native Development',
    description: 'Building high-performance, cross-platform mobile apps that feel native on both iOS and Android using a single codebase.'
  },
  {
    icon: <FaBolt />,
    title: 'App Performance Optimization',
    description: 'Auditing and optimizing existing React Native apps to improve speed, responsiveness, and reduce bundle size.'
  },
  {
    icon: <FaRocket />,
    title: 'App Store Deployment',
    description: 'Handling the entire submission process to the Apple App Store and Google Play Store, ensuring compliance and smooth release.'
  },
  {
    icon: <FaPlug />,
    title: 'API & Backend Integration',
    description: 'Seamless integration with REST APIs, GraphQL, and third-party services like Firebase, Stripe, and health/map SDKs.'
  },
  {
    icon: <FaCoins />,
    title: 'In-App Purchases & Monetization',
    description: 'Implementing subscription flows, AdMob ads, Stripe payments, and coin-based systems to maximize app revenue.'
  },
  {
    icon: <FaPalette />,
    title: 'Mobile UI/UX Design',
    description: 'Crafting pixel-perfect, delightful interfaces using design tokens, animations, and accessibility-first principles.'
  },
];

const InteractiveCard = ({ children, index, glowColor }) => {
  const cardRef = useRef(null);
  const [coords, setCoords] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setCoords({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  return (
    <SpotlightCardWrapper
      ref={cardRef}
      onMouseMove={handleMouseMove}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    >
      <SpotlightEffect x={coords.x} y={coords.y} glowColor={glowColor} />
      {children}
    </SpotlightCardWrapper>
  );
};

const Services = () => {
  return (
    <ServicesSection id="services">
      <Container>
        <SectionHeader>
          <Label>01 / SERVICES</Label>
          <Title>Specialized <span>Services</span></Title>
          <Desc>
            I focus on delivering high-quality mobile solutions that help businesses thrive in the app economy.
          </Desc>
        </SectionHeader>

        <Grid>
          {services.map((service, index) => (
            <InteractiveCard key={index} index={index} glowColor="rgba(99, 102, 241, 0.08)">
              <IconContainer>{service.icon}</IconContainer>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
            </InteractiveCard>
          ))}
        </Grid>
      </Container>
    </ServicesSection>
  );
};

export default Services;